import React, { useEffect, useMemo, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DECISION_STATUSES = ['Approved', 'Rejected', 'Denied'];

const STATUS_COLORS = {
  Approved: 'var(--color-success)',
  Rejected: 'var(--color-error)',
  Denied: 'var(--color-error)',
};

function formatApplicant(application) {
  return (
    `${application.firstName || ''} ${application.lastName || ''}`.trim() ||
    application.legalName ||
    'Unknown applicant'
  );
}

function getDecisionDate(application) {
  const rawValue = application.updatedAt || application.submittedAt || application.createdAt;
  if (!rawValue) return '—';

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

const DecisionsPage = () => {
  const [applications, setApplications] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/applications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load decisions');
        setApplications(await res.json());
      } catch {
        setFetchError('Could not load decision data.');
      }
    };

    if (token) fetchApplications();
  }, [token]);

  const decisions = useMemo(
    () => applications.filter((application) => DECISION_STATUSES.includes(application.status)),
    [applications]
  );

  const approvedCount = decisions.filter((application) => application.status === 'Approved').length;
  const rejectedCount = decisions.filter((application) => application.status === 'Rejected').length;
  const deniedCount = decisions.filter((application) => application.status === 'Denied').length;

  return (
    <div className="page-stack">
      <section className="app-flow-header">
        <div>
          <p className="app-flow-tag">Grant Outcomes</p>
          <h1>Application Decisions</h1>
          <p className="app-flow-description">
            Review final outcomes across the portfolio and keep a cleaner pulse on what has been approved,
            denied, or rejected.
          </p>
        </div>
        <div className="review-summary-cards">
          <div className="review-summary-card">
            <span className="summary-label">Approved</span>
            <strong>{approvedCount}</strong>
          </div>
          <div className="review-summary-card">
            <span className="summary-label">Rejected</span>
            <strong>{rejectedCount}</strong>
          </div>
          <div className="review-summary-card">
            <span className="summary-label">Denied</span>
            <strong>{deniedCount}</strong>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-top">
          <h2>Decision Register</h2>
          <span className="table-meta">{decisions.length} recorded outcomes</span>
        </div>

        {fetchError && <p className="section-error">{fetchError}</p>}

        {!fetchError && (
          <div className="grants-table-wrapper">
            <table className="grants-table">
              <thead>
                <tr>
                  <th>Application #</th>
                  <th>Grant Name</th>
                  <th>Applicant</th>
                  <th>Program</th>
                  <th>Decision</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {decisions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-results">
                      No final decisions have been recorded yet.
                    </td>
                  </tr>
                ) : (
                  decisions.map((application) => (
                    <tr key={application._id || application.id || application.applicationNumber}>
                      <td>{application.applicationNumber || '—'}</td>
                      <td>{application.projectName || application.title || 'Untitled Project'}</td>
                      <td>{formatApplicant(application)}</td>
                      <td>{application.grantProgram || application.programCode || '—'}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: STATUS_COLORS[application.status] || 'var(--text-secondary)' }}
                        >
                          {application.status}
                        </span>
                      </td>
                      <td>{getDecisionDate(application)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default DecisionsPage;
